import { Loading } from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import React, { useMemo, useState } from 'react'
import { FaChevronRight, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { ContractRow, formatDateTime, statusTone } from './types'

type StatusFilter = 'all' | 'awaiting' | 'complete' | 'Sent' | 'Viewed' | 'Signed' | 'Delivered'
type DeliveryFilter = 'all' | 'esign' | 'attachment'

type OriginFilter = 'all' | 'single' | 'batch'

interface SigningTabProps {
    contracts: ContractRow[]
    loaded: boolean
    onRefresh: () => Promise<void> | void
    statusFilterParam: string | null
    onStatusFilterChange: (value: string | null) => void
    onViewBatch?: (batchId: number) => void
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Any status' },
    { value: 'awaiting', label: 'Awaiting signature' },
    { value: 'complete', label: 'Signed or delivered' },
    { value: 'Sent', label: 'Sent, not opened' },
    { value: 'Viewed', label: 'Viewed' },
    { value: 'Signed', label: 'Signed' },
    { value: 'Delivered', label: 'Delivered' },
]

const isStatusFilter = (value: string | null): value is StatusFilter =>
    STATUS_OPTIONS.some((o) => o.value === value)

const matchesStatus = (contract: ContractRow, filter: StatusFilter) => {
    switch (filter) {
        case 'all':
            return true
        case 'awaiting':
            return contract.delivery_type !== 'attachment' && (contract.status === 'Sent' || contract.status === 'Viewed')
        case 'complete':
            return contract.status === 'Signed' || contract.status === 'Delivered'
        default:
            return contract.status === filter
    }
}

const statusLabel = (contract: ContractRow) => {
    if (contract.status === 'Delivered') return 'Delivered'
    if (contract.status === 'Signed') return 'Signed'
    if (contract.status === 'Viewed') return 'Viewed'
    return 'Sent'
}

export const SigningTab = ({
    contracts,
    loaded,
    onRefresh,
    statusFilterParam,
    onStatusFilterChange,
    onViewBatch,
}: SigningTabProps) => {
    const statusFilter: StatusFilter = isStatusFilter(statusFilterParam) ? statusFilterParam : 'all'

    const [search, setSearch] = useState('')
    const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>('all')
    const [originFilter, setOriginFilter] = useState<OriginFilter>('all')
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [busyId, setBusyId] = useState<number | null>(null)

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return contracts.filter((c) => {
            if (!matchesStatus(c, statusFilter)) return false
            if (deliveryFilter !== 'all' && (c.delivery_type || 'esign') !== deliveryFilter) return false
            if (originFilter === 'single' && c.batch_id) return false
            if (originFilter === 'batch' && !c.batch_id) return false
            if (!q) return true
            return [c.recipient_name, c.recipient_email, c.document_name, c.template_name, c.sent_by, c.batch_name, c.case_ref]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        })
    }, [contracts, search, statusFilter, deliveryFilter, originFilter])

    const copyLink = async (link: string) => {
        try {
            await navigator.clipboard.writeText(link)
            Report.success('Copied', 'The signing link has been copied to your clipboard.', 'Okay')
        } catch {
            Report.failure('Copy failed', 'Could not copy the link. Please copy it manually.', 'Okay')
        }
    }

    const openDoc = async (contract: ContractRow, which: 'original' | 'signed') => {
        setBusyId(contract.id)
        try {
            const response = await api({ url: 'contract-sign/get-doc', data: { contract_id: contract.id, which } })
            if (!response.data?.success || !response.data.url) {
                return Report.failure('Error', response.data?.message || 'Could not open the document.', 'Okay')
            }
            window.open(response.data.url, '_blank', 'noopener,noreferrer')
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not open the document.'
            Report.failure('Error', message, 'Okay')
        } finally {
            setBusyId(null)
        }
    }

    const resend = (contract: ContractRow) => {
        const isEsign = contract.delivery_type !== 'attachment'
        Confirm.show(
            'Resend email',
            `Resend the ${isEsign ? 'signing link' : 'document'} to ${contract.recipient_email}?`,
            'Resend',
            'Cancel',
            async () => {
                Loading.circle('Resending...')
                try {
                    const response = await api({ url: 'contract-sign/resend', data: { contract_id: contract.id } })
                    Loading.remove()
                    if (!response.data?.success) {
                        return Report.failure('Error', response.data?.message || 'Could not resend the email.', 'Okay')
                    }
                    if (response.data.emailSent) {
                        Report.success('Sent', `The email has been resent to ${contract.recipient_email}.`, 'Okay')
                    } else {
                        Report.warning('Not delivered', 'The email provider did not accept the message. Try again shortly.', 'Okay')
                    }
                } catch (error: any) {
                    Loading.remove()
                    const message = error?.response?.data?.message || 'Could not resend the email.'
                    Report.failure('Error', message, 'Okay')
                }
            }
        )
    }

    // Inline expansion for a contract: timeline, provenance and the actions,
    // so the table itself only carries the essentials.
    const renderDetail = (contract: ContractRow) => {
        const isEsign = contract.delivery_type !== 'attachment'
        const signed = contract.status === 'Signed'
        const busy = busyId === contract.id
        const stats: { label: string; value: string }[] = [
            { label: 'Sent', value: formatDateTime(contract.created_at) },
            ...(isEsign
                ? [
                      { label: 'Viewed', value: formatDateTime(contract.viewed_at) },
                      { label: 'Signed', value: formatDateTime(contract.signed_at) },
                  ]
                : []),
            { label: 'Sent by', value: contract.sent_by || '—' },
            { label: 'Template', value: contract.template_name || '—' },
            {
                label: 'Client record',
                value: contract.client_origin ? `${contract.client_origin} · #${contract.client_id}` : 'Not linked',
            },
            ...(contract.case_ref ? [{ label: 'Claim', value: contract.case_ref }] : []),
            ...(contract.batch_id
                ? [{ label: 'Bulk send', value: contract.batch_name || `#${contract.batch_id}` }]
                : []),
        ]
        return (
            <div className={styles.miniView}>
                <div className={styles.miniHeader}>
                    <h4 className={styles.miniTitle}>{contract.recipient_name || contract.recipient_email}</h4>
                    <span className={styles.miniMeta}>
                        {isEsign
                            ? signed
                                ? 'Signed copy stored alongside the original'
                                : 'Waiting for the recipient to sign via their personal link'
                            : 'Delivered as a finished PDF attachment'}
                    </span>
                </div>
                <div className={styles.miniStats}>
                    {stats.map((stat) => (
                        <div key={stat.label} className={styles.miniStat}>
                            <span className={styles.miniStatValue}>{stat.value}</span>
                            <span className={styles.miniStatLabel}>{stat.label}</span>
                        </div>
                    ))}
                </div>
                {isEsign && contract.signing_link && !signed && (
                    <>
                        <p className={styles.miniHeading}>Signing link</p>
                        <div className={styles.linkRow}>
                            <a href={contract.signing_link} target="_blank" rel="noopener noreferrer">
                                {contract.signing_link}
                            </a>
                            <button type="button" className={styles.secondaryBtn} onClick={() => copyLink(contract.signing_link!)}>
                                Copy link
                            </button>
                        </div>
                    </>
                )}
                <div className={styles.miniActions}>
                    <button
                        type="button"
                        className={styles.secondaryBtn}
                        disabled={busy}
                        onClick={() => openDoc(contract, 'original')}
                    >
                        {isEsign ? 'Open original' : 'Open PDF'}
                    </button>
                    {signed && (
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            disabled={busy}
                            onClick={() => openDoc(contract, 'signed')}
                        >
                            Open signed copy
                        </button>
                    )}
                    {!signed && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => resend(contract)}>
                            Resend email
                        </button>
                    )}
                    {contract.batch_id && onViewBatch && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => onViewBatch(contract.batch_id!)}>
                            Open bulk send
                        </button>
                    )}
                </div>
            </div>
        )
    }

    return (
        <>
            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by recipient, email, document or sender…"
                />
                <select
                    className={styles.toolbarSelect}
                    value={statusFilter}
                    onChange={(e) => onStatusFilterChange(e.target.value === 'all' ? null : e.target.value)}
                    aria-label="Filter by status"
                >
                    {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={deliveryFilter}
                    onChange={(e) => setDeliveryFilter(e.target.value as DeliveryFilter)}
                    aria-label="Filter by delivery type"
                >
                    <option value="all">Any delivery</option>
                    <option value="esign">Signing link</option>
                    <option value="attachment">PDF attachment</option>
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={originFilter}
                    onChange={(e) => setOriginFilter(e.target.value as OriginFilter)}
                    aria-label="Filter by how it was sent"
                >
                    <option value="all">Single and bulk</option>
                    <option value="single">Single sends</option>
                    <option value="batch">From bulk sends</option>
                </select>
                <button type="button" className={styles.secondaryBtn} onClick={() => onRefresh()}>
                    <FaSyncAlt /> Refresh
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {filtered.length} of {contracts.length} contracts
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Recipient</th>
                            <th>Document</th>
                            <th>Delivery</th>
                            <th>Bulk send</th>
                            <th>Status</th>
                            <th>Sent</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.emptyState}>
                                        {!loaded
                                            ? 'Loading…'
                                            : contracts.length === 0
                                              ? 'No contracts have been sent yet.'
                                              : 'No contracts match this filter.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((contract) => {
                                const isEsign = contract.delivery_type !== 'attachment'
                                const isOpen = selectedId === contract.id
                                return (
                                    <React.Fragment key={contract.id}>
                                        <tr
                                            className={isOpen ? styles.selected : ''}
                                            onClick={() =>
                                                setSelectedId((current) => (current === contract.id ? null : contract.id))
                                            }
                                        >
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        <FaChevronRight
                                                            className={`${styles.rowChevron} ${isOpen ? styles.rowChevronOpen : ''}`}
                                                        />
                                                        {contract.recipient_name || contract.recipient_email}
                                                    </span>
                                                    {contract.recipient_name && (
                                                        <span className={styles.stackSecondary}>{contract.recipient_email}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>{contract.document_name || '—'}</span>
                                                    {contract.template_name && (
                                                        <span className={styles.stackSecondary}>{contract.template_name}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles.pill} ${isEsign ? '' : styles.muted}`}>
                                                    {isEsign ? 'Signing link' : 'Attachment'}
                                                </span>
                                            </td>
                                            <td>
                                                {contract.batch_id ? (
                                                    <div className={styles.stackCell}>
                                                        <span className={styles.stackPrimary}>
                                                            {contract.batch_name || `Bulk send #${contract.batch_id}`}
                                                        </span>
                                                        {contract.case_ref && (
                                                            <span className={styles.stackSecondary}>{contract.case_ref}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className={styles.stackCell}>
                                                        <span className={styles.muted}>Single</span>
                                                        {contract.case_ref && (
                                                            <span className={styles.stackSecondary}>{contract.case_ref}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`${styles.statusPill} ${styles[statusTone(contract.status)]}`}>
                                                    {statusLabel(contract)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={`${styles.stackPrimary} ${styles.nowrap}`}>
                                                        {formatDateTime(contract.created_at)}
                                                    </span>
                                                    <span className={styles.stackSecondary}>{contract.sent_by || '—'}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={6}>{renderDetail(contract)}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}
